import {
  collection,
  deleteDoc,
  doc,
  getDoc,
  setDoc,
} from "firebase/firestore/lite";
import type React from "react";
import { useEffect, useState } from "react";
import ReactDOM from "react-dom";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { Box, MapCard, Transition } from "senf-atomic-design-system";
import { List, useModals } from "senf-atomic-design-system/src";
import Button from "senf-atomic-design-system/src/components/core/button/Button";
import { db, generateFirebaseId } from "senf-shared";
import type { Project } from "senf-shared/types";
import MapBuilder from "./MapBuilder";

export interface NewGis {
  openButton: React.ReactNode;
  gisData: string;
}

type MapDatasetsListProps = {
  openButton?: React.ReactNode;
  show?: boolean;
  setShow?: React.Dispatch<React.SetStateAction<boolean>>;
  project?: Project;
  setProject: (project: Project) => Promise<void>;
  handleUpload: (value: any) => void;
  selectedMapId: string;
};

const MapDatasetsList = ({
  openButton,
  handleUpload,
  selectedMapId,
  project,
  setProject,
  show: showDefault,
  setShow: setShowDefault,
}: MapDatasetsListProps) => {
  const { t } = useTranslation();
  const [show, setShow] = useState(showDefault);
  useEffect(() => {
    setShow(showDefault);
  }, [showDefault]);
  useEffect(() => {
    setShowDefault?.(!!show);
  }, [show, setShowDefault]);
  const { openModal } = useModals();

  const handleSelect = (value) => {
    handleUpload(value);
    setShow(false);
  };

  const onDuplicate = async (data) => {
    const newMapId = generateFirebaseId();
    const mapRef = doc(
      db,
      `organizations/${project?.organizationId}/projects/${project?.projectId}/maps/${data?.mapId}`,
    );

    try {
      const docSnap = await getDoc(mapRef);
      if (docSnap.exists()) {
        const mapData = docSnap.data();
        const newMapData = {
          ...mapData,
          mapId: newMapId,
          title: `${mapData.title} (${t("global:copied")})`,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        };

        await setDoc(
          doc(
            collection(
              db,
              `organizations/${project?.organizationId}/projects/${project?.projectId}/maps`,
            ),
            newMapId,
          ),
          newMapData,
        );

        setProject(project?.projectId);
      }
    } catch (error) {
      console.error("Error duplicating map:", error);
    }
  };

  const onDelete = async (data) => {
    const mapRef = doc(
      db,
      `organizations/${project?.organizationId}/projects/${project?.projectId}/maps/${data?.mapId}`,
    );
    await deleteDoc(mapRef);

    setProject(project?.projectId as any);
  };

  return (
    <>
      {openButton && (
        <div onClick={() => setShow(true)} className="h-full">
          {openButton}
        </div>
      )}
      {ReactDOM.createPortal(
        <Transition
          show={show}
          style={{
            zIndex: 9999,
            right: 0,
            bottom: 0,
            height: "100vh",
            position: "fixed",
            width: "440px",
          }}
          from={{ opacity: 0, scale: 1, x: 200 }}
          leave={{ opacity: 0, scale: 1, x: 300 }}
          enter={{ opacity: 1, scale: 1, x: 0 }}
        >
          <div className="flex h-full w-full flex-col overflow-scroll bg-white shadow-2xl">
            <div className="absolute right-5 top-5 z-10">
              <Button
                leadingIcon="x-md"
                tooltipLabel={t("global:close")}
                onClick={() => setShow(false)}
              />
            </div>
            <div
              className="m-8 mt-12 flex flex-col gap-3"
              id="map-datasets-list"
            >
              <Box flexDirection="column" gap="20px">
                <h3 className="heading-2xl">
                  {t("map:builder.mapDatasetsList.title")}
                </h3>

                <p className="body-md">
                  {t("map:builder.mapDatasetsList.description")}
                </p>
                <Button
                  variant="primary"
                  leadingIcon="plus-md"
                  text={t("util:create", {
                    val: t("util:new", { val: t("global:map") }),
                  })}
                  disabled={project?.maps?.length > 14}
                  disabledNotice={t("map:builder.warning.maxMapsReached")}
                  onClick={() => {
                    setShow(false);
                    openModal(
                      <MapBuilder
                        handleUpload={handleSelect}
                        project={project}
                        setProject={setProject}
                      />,
                      {
                        size: "fullscreen",
                        zIndex: 9999,
                        id: "mapBuilder",
                        transform: false,
                        onClose: false,
                      },
                    );
                  }}
                  // disabled={!formik?.values?.data}
                />

                {project?.maps?.length ? (
                  <h3 className="heading-lg mb-2 mt-6">
                    {t("map:builder.yourMaps")}
                  </h3>
                ) : null}

                <List
                  className="grid grid-cols-2 gap-5"
                  listType="grid"
                  CardType={MapCard}
                  selected={selectedMapId}
                  lightCard={true}
                  data={project?.maps}
                  onClick={(data) => handleSelect(data?.mapId)}
                  onEdit={(data) => {
                    setShow(false);
                    openModal(
                      <MapBuilder
                        handleUpload={handleSelect}
                        mapId={data?.mapId}
                        project={project}
                        setProject={setProject}
                      />,
                      {
                        size: "fullscreen",
                        zIndex: 9999,
                        id: "mapBuilder",
                        transform: false,
                        onClose: false,
                      },
                    );
                  }}
                  onDuplicate={onDuplicate}
                  onDelete={onDelete}
                  duplicatable={project?.maps?.length <= 14}
                />
              </Box>
            </div>
          </div>
        </Transition>,
        document.body,
      )}
    </>
  );
};

export default MapDatasetsList;
